// @ts-nocheck
import React from 'react';
import { OnboardRecord, STATUS_CONFIG, timeAgo } from './types';

interface Props {
  record: OnboardRecord;
  onTap: (record: OnboardRecord) => void;
}

// ── Status Icon ──────────────────────────────────────────────────────────

const StatusIcon: React.FC<{ status: string }> = ({ status }) => {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending;

  if (config.icon === 'spinner') {
    return (
      <div className="relative w-10 h-10 shrink-0">
        <div className="absolute inset-0 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center">
          <svg className="w-4 h-4 text-white animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        </div>
        <div className="absolute inset-0 rounded-full border-2 border-blue-400/30 animate-ping" />
      </div>
    );
  }

  if (config.icon === 'arrow-up') {
    return (
      <div className="relative w-10 h-10 shrink-0">
        <div className="absolute inset-0 rounded-full bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center">
          <svg className="w-4 h-4 text-white animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 10l7-7m0 0l7 7m-7-7v18" />
          </svg>
        </div>
        <svg className="absolute inset-0 w-10 h-10 animate-spin" style={{ animationDuration: '3s' }} viewBox="0 0 40 40">
          <circle cx="20" cy="20" r="18" fill="none" stroke="rgba(147,51,234,0.2)" strokeWidth="2" />
          <circle cx="20" cy="20" r="18" fill="none" stroke="rgba(147,51,234,0.6)" strokeWidth="2" strokeDasharray="28 85" strokeLinecap="round" />
        </svg>
      </div>
    );
  }

  if (config.icon === 'check') {
    return (
      <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center shrink-0">
        <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
        </svg>
      </div>
    );
  }

  if (config.icon === 'x-circle') {
    return (
      <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center shrink-0">
        <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </div>
    );
  }

  if (config.icon === 'check-circle') {
    return (
      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-400 to-emerald-600 flex items-center justify-center shrink-0">
        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
        </svg>
      </div>
    );
  }

  // clock (pending)
  return (
    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shrink-0">
      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    </div>
  );
};

// ── Fetching Progress Bar ────────────────────────────────────────────────

const FetchingProgress: React.FC = () => (
  <div className="mt-2">
    <div className="h-1 bg-blue-100 rounded-full overflow-hidden">
      <div className="h-full bg-blue-500 rounded-full animate-progress-indeterminate" />
    </div>
    <p className="text-[10px] text-blue-500 mt-1 font-medium">Scraper is fetching posts...</p>
  </div>
);

// ── Importing Progress Bar ───────────────────────────────────────────────

const ImportingProgress: React.FC = () => (
  <div className="mt-2">
    <div className="h-1 bg-purple-100 rounded-full overflow-hidden">
      <div className="h-full bg-purple-500 rounded-full animate-progress-indeterminate" />
    </div>
    <p className="text-[10px] text-purple-500 mt-1 font-medium">Writing posts to shop...</p>
  </div>
);

// ── Main QueueItem ───────────────────────────────────────────────────────

const QueueItem: React.FC<Props> = ({ record, onTap }) => {
  const config = STATUS_CONFIG[record.status] || STATUS_CONFIG.pending;
  const isReady = record.status === 'ready' && (record.postCount || 0) > 0;
  const isDone = record.status === 'done';
  const isError = record.status === 'error';
  const isFetching = record.status === 'fetching';
  const isImporting = record.status === 'importing';

  return (
    <div
      onClick={() => onTap(record)}
      className={`bg-white rounded-xl border px-4 py-3.5 transition-all duration-200 ${
        isReady
          ? 'border-green-200 cursor-pointer active:bg-green-50 hover:border-green-400 hover:shadow-sm'
          : isDone
            ? 'border-gray-100 opacity-60'
            : isError
              ? 'border-red-200'
              : isFetching
                ? 'border-blue-200'
                : isImporting
                  ? 'border-purple-200'
                  : 'border-gray-200'
      }`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <StatusIcon status={record.status} />
          <div className="min-w-0">
            <p className="font-semibold text-gray-900 text-sm truncate">@{record.username}</p>
            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold ${config.bg} ${config.text}`}>
                {config.label}
              </span>
              {record.postCount > 0 && (
                <span className="text-[11px] text-gray-400 font-medium">
                  {record.postCount} {record.postCount === 1 ? 'post' : 'posts'}
                </span>
              )}
              {record.shopId && isDone && (
                <span className="text-[10px] text-gray-400 font-mono bg-gray-50 px-1.5 py-0.5 rounded">
                  {record.shopId.slice(0, 8)}…
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0 ml-2">
          <span className="text-[11px] text-gray-400">{timeAgo(record.createdAt)}</span>
          {isReady && (
            <div className="w-7 h-7 rounded-full bg-green-100 flex items-center justify-center">
              <svg className="w-3.5 h-3.5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          )}
        </div>
      </div>

      {/* Progress bars */}
      {isFetching && <FetchingProgress />}
      {isImporting && <ImportingProgress />}

      {/* Ready CTA */}
      {isReady && (
        <div className="mt-2.5 flex items-center gap-2 px-3 py-2 bg-green-50 rounded-lg border border-green-100">
          <svg className="w-4 h-4 text-green-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <span className="text-xs text-green-700 font-semibold">Tap to select posts & create shop</span>
        </div>
      )}

      {/* Error detail */}
      {isError && record.error && (
        <div className="mt-2 flex items-start gap-2 px-3 py-2 bg-red-50 rounded-lg border border-red-100">
          <svg className="w-3.5 h-3.5 text-red-500 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="text-[11px] text-red-600 leading-tight">{record.error}</span>
        </div>
      )}
    </div>
  );
};

export default QueueItem;
